/**
 * /admin/analysis — what the responses say about the QUESTIONS.
 *
 * The review queue already asks a person whether a question reads correctly.
 * That catches typos and nonsense, and it cannot catch the failure that
 * matters most, because that one reads perfectly to whoever wrote it: a wrong
 * answer key. A question keyed to the wrong option passes review, then quietly
 * takes a mark off everyone who understood it and gives one to everyone who
 * did not.
 *
 * The response data exposes it. If the students who did best on the rest of
 * the paper are the ones getting this question wrong, the question is not hard
 * — it is broken. That is the `discrimination` column, and it is the reason
 * this screen exists; everything else here is context for reading it.
 *
 * Two rules govern the whole page, and both exist because the failure mode of
 * item analysis is a confident-looking statistic computed from four students:
 *
 *   1. Nothing is ever asserted more strongly than the cohort supports. Every
 *      row carries its confidence, and an `insufficient` row is drawn muted
 *      and says out loud that it is not to be acted on.
 *   2. A null statistic is never rendered as a zero. Null means "there was
 *      nothing to measure" — nobody answered, or nobody got it wrong — which
 *      is a different fact from "measured, and it discriminates poorly".
 *
 * The statistics themselves live in `apps/api/src/lib/item-stats.ts`; the
 * thresholds mirrored here are read off that file and must not drift from it.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  KeyRound,
  TriangleAlert,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import type { BadgeTone } from "@/components/ui";
import { ITEM_FLAG_LABEL, ITEM_STATS_PROVISIONAL_N } from "@exam/shared";
import type {
  ItemAnalysisResponse,
  ItemFlag,
  ItemStats,
  OptionBreakdown,
  StatConfidence,
} from "@exam/shared";
import { cn } from "@/lib/cn";
import { useCatalog } from ".";
import { ErrorNote, FixtureBanner, Loading, PageTitle, Stat } from "./components/Shell";
import {
  fetchAttempts,
  fetchExams,
  fetchItemAnalysis,
  withdrawQuestion,
} from "./data/client";
import type { ExamRow } from "./data/types";
import { TYPE_LABEL } from "./lib/enumLabels";
import { formatDuration, formatPercent } from "./lib/format";
import { useAsync } from "./lib/useAsync";

/* ------------------------------------------------------------------ */
/* Thresholds — mirrored from apps/api/src/lib/item-stats.ts           */
/* ------------------------------------------------------------------ */

/**
 * The screen does not decide what counts as mis-keyed — it colours what the
 * API already decided.
 *
 * It used to copy the cut-point (−0.05). Then the API split it by sample size:
 * a full cohort still trips at −0.05, a provisional one needs −0.30, because on
 * twelve students a sound question comes back at −0.15 from noise alone. The
 * copy here did not move, so the screen painted a red, bold, "the strong
 * students are getting this wrong" number on questions the API had
 * deliberately declined to flag — red without a badge, which is worse than
 * either state on its own.
 *
 * Reading the flag instead of re-deriving the rule makes that drift
 * impossible. `WEAK_BELOW` stays a local constant because it only picks an
 * amber tone for a value the API describes with `not_discriminating`, and
 * getting it slightly wrong changes a shade, not a claim.
 */
const WEAK_BELOW = 0.15;

/** Fewer than this many responses and the API flags nothing at all. */
const PROVISIONAL_N = ITEM_STATS_PROVISIONAL_N;

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function ItemAnalysisPage() {
  const catalog = useCatalog();
  const exams = useAsync(() => fetchExams(catalog), [catalog]);

  /**
   * Read only to choose a sensible default exam and to say, in the picker,
   * which papers have actually been sat. The analysis endpoint answers for one
   * paper at a time, and landing on a paper nobody has sat would show an empty
   * screen to someone who has a perfectly good set of results one dropdown
   * click away.
   */
  const attempts = useAsync(() => fetchAttempts(), []);

  const gradedByExam = useMemo(() => {
    const acc = new Map<string, number>();
    for (const row of attempts.data?.attempts ?? []) {
      // The same definition the analysis itself uses: only a graded sitting
      // counts. An in-progress attempt's answers are still being changed.
      if (row.attempt.status !== "submitted" && row.attempt.status !== "auto_submitted") {
        continue;
      }
      acc.set(row.attempt.exam_id, (acc.get(row.attempt.exam_id) ?? 0) + 1);
    }
    return acc;
  }, [attempts.data]);

  const ordered = useMemo(() => orderExams(exams.data ?? []), [exams.data]);

  // Most recent paper that somebody has actually sat; failing that, the most
  // recent paper at all, so the picker is never empty while exams exist.
  const defaultExamId = useMemo(() => {
    const sat = ordered.find((e) => (gradedByExam.get(e.id) ?? 0) > 0);
    return sat?.id ?? ordered[0]?.id ?? null;
  }, [ordered, gradedByExam]);

  // `null` means "the user has not chosen", which is not the same as "no exam"
  // — it lets the default follow the exam list once it arrives.
  const [chosenExamId, setChosenExamId] = useState<string | null>(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const examId = chosenExamId ?? defaultExamId;

  const analysis = useAsync(
    // Always fetched UNFILTERED, and narrowed below.
    //
    // Asking the API for the flagged subset made every whole-paper figure on
    // this screen quietly become a figure about the flagged subset: the
    // question count said "3" for a thirty-question paper, and the mean time
    // each row is compared against was the mean of the flagged rows alone —
    // while the tooltip called it "the paper's average". A paper is tens of
    // questions, so there is nothing to save by filtering server-side.
    () => fetchItemAnalysis({ examId: examId ?? undefined }),
    [examId],
    { enabled: examId !== null },
  );

  const examTitle =
    analysis.data?.exam_title ?? ordered.find((e) => e.id === examId)?.title ?? null;

  return (
    <div className="space-y-4">
      <PageTitle
        title="Item analysis"
        meta="What the responses say about the questions. The review queue asks whether a question reads correctly; this asks whether it behaved correctly."
        actions={
          <Button
            variant={flaggedOnly ? "primary" : "secondary"}
            aria-pressed={flaggedOnly}
            onClick={() => setFlaggedOnly((on) => !on)}
          >
            {flaggedOnly ? "Flagged only" : "Show all questions"}
          </Button>
        }
      />

      <FixtureBanner />

      <Card>
        <CardHeader
          title="Paper"
          description="Statistics are per paper: the same question on two papers sits among different company, and merging them would average away the difference someone is looking for."
          actions={
            <Select
              label="Exam"
              value={examId ?? ""}
              disabled={ordered.length === 0}
              onChange={(e) => setChosenExamId(e.target.value)}
              className="w-80"
            >
              {ordered.map((exam) => {
                const graded = gradedByExam.get(exam.id) ?? 0;
                return (
                  <option key={exam.id} value={exam.id}>
                    {exam.title}
                    {graded > 0 ? ` — ${graded} sat` : " — not sat yet"}
                  </option>
                );
              })}
            </Select>
          }
        />

        {exams.error ? (
          <div className="p-4">
            <ErrorNote error={exams.error} />
          </div>
        ) : exams.loading ? (
          <Loading label="Reading the exam list…" />
        ) : ordered.length === 0 ? (
          <p className="px-4 py-6 text-xs text-ink-faint">
            No exams exist yet. Build a paper on{" "}
            <Link to="/admin/exams" className="text-accent hover:underline">
              Exams
            </Link>
            , schedule it, and this screen fills in once students have sat it.
          </p>
        ) : null}
      </Card>

      {examId === null ? null : analysis.error ? (
        <ErrorNote error={analysis.error} />
      ) : analysis.loading ? (
        <Loading label="Computing item statistics…" />
      ) : analysis.data ? (
        <Analysis
          data={analysis.data}
          examTitle={examTitle}
          flaggedOnly={flaggedOnly}
          onShowAll={() => setFlaggedOnly(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Newest paper first. `scheduled_at` is null for a draft and for a
 * `from_start` paper that has not been given a date, and those sort last
 * rather than first — an undated draft is the least likely thing anyone came
 * here to read.
 */
function orderExams(exams: readonly ExamRow[]): ExamRow[] {
  return [...exams].sort((a, b) => {
    const at = a.scheduled_at ?? -Infinity;
    const bt = b.scheduled_at ?? -Infinity;
    return bt - at || a.title.localeCompare(b.title);
  });
}

/* ------------------------------------------------------------------ */
/* Headline: the cohort, and whether the paper holds together          */
/* ------------------------------------------------------------------ */

function Analysis({
  data,
  examTitle,
  flaggedOnly,
  onShowAll,
}: {
  data: ItemAnalysisResponse;
  examTitle: string | null;
  flaggedOnly: boolean;
  onShowAll: () => void;
}) {
  const misKeyed = data.items.filter((i) => i.flags.includes("mis_keyed")).length;
  const flagged = data.items.filter((i) => i.flags.length > 0).length;
  // The rows on screen; every count and baseline above uses the whole paper.
  const shown = flaggedOnly
    ? data.items.filter((i) => i.flags.length > 0)
    : data.items;

  /**
   * The paper's own average time per question, so a slow item can be described
   * relative to its neighbours rather than in absolute seconds — "70 seconds"
   * means nothing until you know the paper averages 25.
   */
  const paperMeanTimeMs = useMemo(() => {
    const times = data.items
      .map((i) => i.mean_time_ms)
      .filter((t): t is number => t !== null);
    if (times.length === 0) return null;
    return times.reduce((a, b) => a + b, 0) / times.length;
  }, [data.items]);

  const reading = readReliability(data.reliability);

  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-4">
          <Stat
            label="Cohort"
            value={data.cohort_size}
            hint={
              data.cohort_size === 0
                ? "Nobody has sat this paper"
                : "Graded sittings only"
            }
            tone={data.cohort_size < PROVISIONAL_N ? "review" : "default"}
          />
          <Stat label="Questions" value={data.items.length} hint={examTitle ?? undefined} />
          <Stat
            label="Flagged"
            value={flagged}
            hint={flagged === 0 ? "Nothing to look at" : `of ${data.items.length} shown`}
            tone={flagged > 0 ? "review" : "default"}
          />
          <Stat
            label="Key may be wrong"
            value={misKeyed}
            hint={misKeyed === 0 ? "None" : "Read the key before the question"}
            tone={misKeyed > 0 ? "violation" : "default"}
            icon={misKeyed > 0 ? TriangleAlert : undefined}
          />
        </div>

        {/* Reliability sits under the numbers rather than beside them because
            it is a sentence, not a figure: the number on its own tells nobody
            anything, and a bare 0.41 looks reassuringly precise. */}
        <div className="flex items-start gap-2 border-t border-border px-4 py-3">
          <span className={cn("tabular text-lg font-semibold", reading.tone)}>
            {data.reliability === null ? "—" : data.reliability.toFixed(2)}
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-ink">
              Reliability (KR-20) — {reading.headline}
            </p>
            <p className="mt-0.5 text-2xs text-ink-muted">{reading.detail}</p>
          </div>
        </div>
      </Card>

      {data.cohort_size === 0 ? (
        <Card>
          <p className="px-4 py-6 text-xs text-ink-faint">
            Nobody has sat this paper yet, so there is nothing to analyse. Item
            statistics are computed from graded sittings only — an in-progress
            attempt's answers are still being changed. Pick a paper that has been
            sat, or come back once this one has been graded.
          </p>
        </Card>
      ) : data.cohort_size < PROVISIONAL_N ? (
        <div className="flex items-start gap-2 rounded-md border border-review/30 bg-review-soft px-3 py-2">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0 text-review" />
          <p className="text-xs text-ink">
            {data.cohort_size} graded{" "}
            {data.cohort_size === 1 ? "sitting" : "sittings"} — below the{" "}
            {PROVISIONAL_N} needed for any statistic to mean something, so
            nothing on this paper is flagged and nothing below should be acted
            on. The numbers are shown because hiding them would be its own kind
            of lie; they are arithmetic, not evidence.
          </p>
        </div>
      ) : null}

      <ItemTable
        items={shown}
        cohortSize={data.cohort_size}
        paperMeanTimeMs={paperMeanTimeMs}
        flaggedOnly={flaggedOnly}
        onShowAll={onShowAll}
      />
    </div>
  );
}

/**
 * A number nobody can read is worse than no number, so KR-20 is always shown
 * with what it means. The bands are the ones documented on `reliability()` in
 * `item-stats.ts`.
 */
function readReliability(value: number | null): {
  headline: string;
  detail: string;
  tone: string;
} {
  if (value === null) {
    return {
      headline: "not computed",
      detail:
        "Reliability is a property of one paper measured across its students, so it needs a single exam selected and more than one student who sat it — and item marks that actually vary. One of those is missing.",
      tone: "text-ink-faint",
    };
  }
  if (value < 0.5) {
    return {
      headline: "the total score is close to noise",
      detail:
        "The questions are not agreeing with each other about who is strong. Ranking students by their total on this paper is not defensible — start with the flagged items below.",
      tone: "text-violation",
    };
  }
  if (value < 0.8) {
    return {
      headline: "usable, but not something to rank people by finely",
      detail:
        "The paper broadly measures one thing. Fixing the flagged items below is the fastest way to move this above 0.8, where a total score can be defended.",
      tone: "text-review",
    };
  }
  return {
    headline: "the paper holds together",
    detail:
      "The questions agree with each other about who is strong, so a total score on this paper means something. Individual flagged items below are still worth reading.",
    tone: "text-answered",
  };
}

/* ------------------------------------------------------------------ */
/* The item table                                                      */
/* ------------------------------------------------------------------ */

const COLUMNS = 9;

function ItemTable({
  items,
  cohortSize,
  paperMeanTimeMs,
  flaggedOnly,
  onShowAll,
}: {
  items: readonly ItemStats[];
  cohortSize: number;
  paperMeanTimeMs: number | null;
  flaggedOnly: boolean;
  onShowAll: () => void;
}) {
  // One row open at a time. The expansion is tall and the point of the screen
  // is comparison down the discrimination column, which several open rows
  // would push apart.
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader
        title="Questions, worst first"
        description="Ordered by the API: a question whose key may be wrong first, then anything else flagged, then by discrimination."
      />

      {/* The legend is not decoration. "Difficulty" going UP as the question
          gets EASIER is the convention in classical test theory and it is
          read backwards by everyone who has not met it before. */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 border-b border-border bg-surface-sunken px-4 py-2 text-2xs text-ink-muted">
        <span>
          <strong className="font-medium text-ink">Difficulty</strong> — share of
          those who answered who got it right. High means EASY.
        </span>
        <span>
          <strong className="font-medium text-ink">Discrimination</strong> — how
          strongly getting this right goes with doing well on the rest of the
          paper. Negative means the strong students are the ones getting it
          wrong.
        </span>
      </div>

      <Table>
        <THead>
          <TR>
            <TH className="w-8" />
            <TH align="right" className="w-12">
              #
            </TH>
            <TH>Question</TH>
            <TH className="w-16">Type</TH>
            <TH align="right" className="w-28">
              Difficulty
              <span className="ml-1 font-normal text-ink-faint">(high = easy)</span>
            </TH>
            <TH align="right" className="w-28">
              Discrimination
            </TH>
            <TH align="right" className="w-16">
              Skipped
            </TH>
            <TH align="right" className="w-24">
              Mean time
            </TH>
            <TH className="w-64">Flags &amp; confidence</TH>
          </TR>
        </THead>
        <TBody>
          {items.length === 0 ? (
            <TableEmpty colSpan={COLUMNS}>
              {flaggedOnly ? (
                <>
                  No question on this paper carries a flag — on the responses so
                  far, nothing here needs rewriting.{" "}
                  <button
                    type="button"
                    onClick={onShowAll}
                    className="text-accent hover:underline"
                  >
                    Show all questions
                  </button>
                  .
                </>
              ) : cohortSize === 0 ? (
                "No graded sittings, so no items to analyse."
              ) : (
                "This paper has no questions on it."
              )}
            </TableEmpty>
          ) : (
            items.map((item) => (
              <ItemRow
                key={`${item.exam_id}:${item.question_id}`}
                item={item}
                paperMeanTimeMs={paperMeanTimeMs}
                open={openId === item.question_id}
                onToggle={() =>
                  setOpenId((current) =>
                    current === item.question_id ? null : item.question_id,
                  )
                }
              />
            ))
          )}
        </TBody>
      </Table>
    </Card>
  );
}

function ItemRow({
  item,
  paperMeanTimeMs,
  open,
  onToggle,
}: {
  item: ItemStats;
  paperMeanTimeMs: number | null;
  open: boolean;
  onToggle: () => void;
}) {
  const misKeyed = item.flags.includes("mis_keyed");
  // Below five responses the API flags nothing, so a row here carries numbers
  // that look like the others and mean much less. It is drawn faint for the
  // same reason a draft is: so the eye skips it while scanning for a problem.
  const weak = item.confidence === "insufficient";

  return (
    <>
      <TR
        className={cn(
          "cursor-pointer",
          weak && "opacity-55",
          misKeyed && !weak && "bg-violation-soft/40",
        )}
        onClick={onToggle}
      >
        <TD>
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} the option breakdown for question ${item.order_idx + 1}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="flex size-5 items-center justify-center rounded text-ink-faint hover:bg-surface-sunken hover:text-ink"
          >
            {open ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        </TD>

        <TD align="right" className="tabular text-ink-muted">
          {item.order_idx + 1}
        </TD>

        <TD className="max-w-0">
          <p className="truncate text-ink" title={item.summary}>
            {item.summary || "(no stem text)"}
          </p>
          <p className="truncate text-2xs text-ink-faint">
            {item.topic_name} · {item.answered} of {item.presented} answered
          </p>
        </TD>

        <TD className="text-ink-muted">{TYPE_LABEL[item.type]}</TD>

        <TD align="right">
          <DifficultyCell item={item} />
        </TD>

        <TD align="right">
          <DiscriminationCell
            value={item.discrimination}
            answered={item.answered}
            misKeyed={item.flags.includes("mis_keyed")}
          />
        </TD>

        <TD align="right" className="tabular text-ink-muted">
          {formatPercent(item.skip_rate)}
        </TD>

        <TD align="right">
          <MeanTimeCell ms={item.mean_time_ms} paperMeanTimeMs={paperMeanTimeMs} />
        </TD>

        <TD>
          <div className="flex flex-wrap items-center gap-1">
            {item.flags.map((flag) => (
              <FlagBadge key={flag} flag={flag} />
            ))}
            <ConfidenceBadge confidence={item.confidence} answered={item.answered} />
          </div>
        </TD>
      </TR>

      {open ? (
        <tr className="border-b border-border bg-surface-sunken last:border-b-0">
          <td colSpan={COLUMNS} className="px-4 py-3">
            <ItemDetail item={item} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Cells                                                               */
/* ------------------------------------------------------------------ */

function DifficultyCell({ item }: { item: ItemStats }) {
  if (item.difficulty === null) {
    return (
      <span
        className="text-ink-faint"
        title="Nobody answered this question, so there is no difficulty to compute. This is not a difficulty of zero."
      >
        —
      </span>
    );
  }
  return (
    <span className="tabular text-ink" title={`${item.correct} of ${item.answered} answered it correctly`}>
      {item.difficulty.toFixed(2)}
    </span>
  );
}

/**
 * The column the screen is for.
 *
 * Colour follows the same cut-points the API flags on, so a red number is
 * always a number that earned a `mis_keyed` badge. The sign and the value are
 * printed either way — nothing here is carried by hue alone, and a negative
 * sign in front of a bold number reads on a monochrome print-out.
 */
function DiscriminationCell({
  value,
  answered,
  misKeyed,
}: {
  value: number | null;
  answered: number;
  /** Whether the API flagged it — the screen colours the flag, not the value. */
  misKeyed: boolean;
}) {
  if (value === null) {
    return (
      <span
        className="text-ink-faint"
        title={
          answered === 0
            ? "Nobody answered this question, so there was nothing to correlate. This is not a discrimination of zero."
            : "Every student who answered got the same result, so there was nothing to correlate — the item carries no information about who is strong. This is not a discrimination of zero."
        }
      >
        —
      </span>
    );
  }

  // Red is reserved for the value that earned the badge. A negative number the
  // API did not flag is still worth seeing — it is shown amber, as the weak
  // discrimination it is, rather than as an accusation the API refused to make.
  const tone = misKeyed
    ? "text-violation font-semibold"
    : value < WEAK_BELOW
      ? "text-review"
      : "text-ink";

  return (
    <span
      className={cn("tabular", tone)}
      title={
        misKeyed
          ? "Negative: the students doing best on the rest of the paper are getting this one wrong."
          : value < 0
            ? "Slightly negative, but not beyond what this sample size can produce by chance — reported, not flagged."
            : undefined
      }
    >
      {value < 0 ? "−" : "+"}
      {Math.abs(value).toFixed(2)}
    </span>
  );
}

function MeanTimeCell({
  ms,
  paperMeanTimeMs,
}: {
  ms: number | null;
  paperMeanTimeMs: number | null;
}) {
  if (ms === null) {
    return (
      <span className="text-ink-faint" title="No timed response was recorded for this question.">
        —
      </span>
    );
  }
  // Seconds mean nothing without the paper to compare against, so a
  // conspicuously slow or fast item says so in the cell.
  const ratio = paperMeanTimeMs && paperMeanTimeMs > 0 ? ms / paperMeanTimeMs : null;
  const note =
    ratio === null ? null : ratio >= 1.6 ? "slow" : ratio <= 0.5 ? "quick" : null;

  return (
    <span className="tabular text-ink-muted" title={ratio ? `${ratio.toFixed(1)}× the paper's average` : undefined}>
      {formatDuration(ms)}
      {note ? <span className="ml-1 text-2xs text-ink-faint">{note}</span> : null}
    </span>
  );
}

const FLAG_TONE: Record<ItemFlag, BadgeTone> = {
  mis_keyed: "violation",
  not_discriminating: "review",
  too_easy: "neutral",
  too_hard: "neutral",
  dead_distractor: "neutral",
  attractive_distractor: "review",
  often_skipped: "neutral",
};

function FlagBadge({ flag }: { flag: ItemFlag }) {
  const label = ITEM_FLAG_LABEL[flag];
  if (flag === "mis_keyed") {
    // The one flag worth building the feature for, so it does not look like
    // the others: it carries an icon, it says what to DO, and it is the only
    // badge on the row rendered in the violation token.
    return (
      <Badge tone="violation" className="font-semibold">
        <TriangleAlert className="size-3" aria-hidden />
        {label} — read the key before you read the question
      </Badge>
    );
  }
  return <Badge tone={FLAG_TONE[flag]}>{label}</Badge>;
}

const CONFIDENCE_TONE: Record<StatConfidence, BadgeTone> = {
  insufficient: "neutral",
  provisional: "neutral",
  reliable: "neutral",
};

function ConfidenceBadge({
  confidence,
  answered,
}: {
  confidence: StatConfidence;
  answered: number;
}) {
  const text: Record<StatConfidence, string> = {
    insufficient: `${answered} answered — not enough to act on`,
    provisional: `${answered} answered — provisional`,
    reliable: `${answered} answered`,
  };
  return (
    <Badge
      tone={CONFIDENCE_TONE[confidence]}
      className={confidence === "insufficient" ? "italic" : undefined}
    >
      {text[confidence]}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* The expansion: which option pulled whom                             */
/* ------------------------------------------------------------------ */

/**
 * The evidence behind the flag.
 *
 * The single most useful number on this screen is the mean overall score of
 * the students who chose each option. If a wrong option's choosers outscore
 * the key's choosers, the question is not measuring what it claims to — very
 * often because the key is on the wrong option and the one below it is the
 * real answer. That comparison is what the layout is built around: the key row
 * and any row that beat it are the two rows the eye lands on.
 */
/**
 * Doing something about what the analysis found.
 *
 * Without this the screen is half a feature: it tells you a question's answer
 * key is probably wrong, and the same question goes onto the next auto-picked
 * paper, to a different cohort, and takes another mark off everyone who
 * understood it.
 *
 * Withdrawing is deliberately not rejecting. The likeliest fix is correcting
 * the key, not deleting a question that may be perfectly good — so it goes back
 * to the review queue, leaves the auto-pick pool, and papers that already
 * contain it are untouched. Changing a paper people have already sat is never
 * the right response to noticing a problem with it.
 */
function WithdrawAction({ item }: { item: ItemStats }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Never offered on a sample too small to mean anything — the analysis
  // flags nothing below that threshold, so acting on it would be acting on
  // arithmetic.
  if (item.confidence === "insufficient" || item.flags.length === 0) return null;

  if (done) {
    return (
      <p className="text-2xs text-answered">
        Sent back to the review queue. It will not be auto-picked onto a new
        paper until someone approves it again.
      </p>
    );
  }

  const run = async () => {
    setBusy(true);
    try {
      await withdrawQuestion(item.question_id);
      setDone(true);
      toast({
        tone: "success",
        title: "Question withdrawn.",
        description: "It is back in the review queue and out of the pick pool.",
      });
    } catch (err) {
      toast({
        tone: "error",
        title: "Could not withdraw it.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
      <Button size="sm" disabled={busy} onClick={() => void run()}>
        {busy ? "Withdrawing…" : "Send back to review"}
      </Button>
      <span className="text-2xs text-ink-faint">
        Takes it out of the auto-pick pool. Papers that already use it are
        unchanged, and their marks stand.
      </span>
    </div>
  );
}

function ItemDetail({ item }: { item: ItemStats }) {
  const key = item.options.find((o) => o.is_correct) ?? null;
  const keyScore = key?.mean_score_pct ?? null;
  const beating =
    keyScore === null
      ? []
      : item.options.filter(
          (o) => !o.is_correct && o.mean_score_pct !== null && o.mean_score_pct > keyScore,
        );

  const maxShare = item.options.reduce((m, o) => Math.max(m, o.share), 0);

  return (
    <div className="space-y-3">
      {item.confidence === "insufficient" ? (
        <p className="text-2xs text-ink-muted">
          Only {item.answered}{" "}
          {item.answered === 1 ? "student" : "students"} answered this question.
          Nothing below is flagged and nothing below should be acted on — under{" "}
          {PROVISIONAL_N} responses these are arithmetic, not evidence.
        </p>
      ) : null}

      {beating.length > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-violation/30 bg-violation-soft px-3 py-2">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-violation" />
          <p className="text-xs text-ink">
            {beating.length === 1
              ? `Option ${beating[0]!.key} is marked wrong, but the students who chose it are averaging `
              : `${beating.length} options marked wrong were chosen by students averaging `}
            {beating.length === 1
              ? `${beating[0]!.mean_score_pct!.toFixed(1)}% on the paper as a whole — above the ${keyScore!.toFixed(1)}% of the students who picked the key. `
              : `above the ${keyScore!.toFixed(1)}% of the students who picked the key. `}
            That is what a wrong answer key looks like from the outside. Check
            the key before assuming the question is merely ambiguous.
          </p>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-border bg-surface">
        <table className="w-full border-collapse text-xs">
          <thead className="bg-surface-sunken text-ink-muted">
            <tr>
              <th scope="col" className="h-8 border-b border-border px-3 text-left font-medium">
                Option
              </th>
              <th scope="col" className="h-8 w-40 border-b border-border px-3 text-left font-medium">
                Chosen
              </th>
              <th scope="col" className="h-8 w-16 border-b border-border px-3 text-right font-medium">
                n
              </th>
              <th scope="col" className="h-8 w-52 border-b border-border px-3 text-right font-medium">
                Mean score of who chose it
              </th>
            </tr>
          </thead>
          <tbody>
            {item.options.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-xs text-ink-faint">
                  {item.type === "NAT"
                    ? "A numeric-answer question has no options, so there is no distractor breakdown. Its difficulty and discrimination above are still computed the same way."
                    : "This question has no options recorded."}
                </td>
              </tr>
            ) : (
              item.options.map((option) => (
                <OptionRow
                  key={option.key}
                  option={option}
                  keyScore={keyScore}
                  maxShare={maxShare}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4">
        <p className="text-2xs text-ink-faint">
          {item.presented - item.answered} of {item.presented} left this blank.
        </p>
        {/*
          The bank has no per-question route — `/admin/questions` holds its own
          selection in component state and there is no `:questionId` segment on
          it (see the route list in `pages/admin/index.tsx`). Rather than invent
          a URL that would 404, this links to the bank and names the question so
          it can be found there. The day a detail route exists, this is the one
          line to change.
        */}
        <Link
          to="/admin/questions"
          className="inline-flex items-center gap-1 text-2xs text-accent hover:underline"
        >
          Open the question bank
          <ExternalLink className="size-3" aria-hidden />
        </Link>
      </div>

      <WithdrawAction item={item} />
    </div>
  );
}

function OptionRow({
  option,
  keyScore,
  maxShare,
}: {
  option: OptionBreakdown;
  keyScore: number | null;
  maxShare: number;
}) {
  const beatsKey =
    !option.is_correct &&
    keyScore !== null &&
    option.mean_score_pct !== null &&
    option.mean_score_pct > keyScore;

  return (
    <tr
      className={cn(
        "border-b border-border last:border-b-0",
        option.is_correct && "bg-answered-soft",
        beatsKey && "bg-violation-soft",
      )}
    >
      <td className="px-3 py-2 align-top">
        <div className="flex items-start gap-2">
          <span className="tabular mt-0.5 shrink-0 font-medium text-ink-muted">
            {option.key}
          </span>
          <div className="min-w-0">
            <p className="text-ink">{option.text}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {option.is_correct ? (
                <Badge tone="answered">
                  <KeyRound className="size-3" aria-hidden />
                  Answer key
                </Badge>
              ) : null}
              {beatsKey ? (
                <Badge tone="violation">
                  <TriangleAlert className="size-3" aria-hidden />
                  Outscores the key by{" "}
                  {(option.mean_score_pct! - keyScore!).toFixed(1)} pts
                </Badge>
              ) : null}
              {option.chosen === 0 ? (
                <Badge tone="neutral">Nobody chose this</Badge>
              ) : null}
            </div>
          </div>
        </div>
      </td>

      <td className="px-3 py-2 align-top">
        <ShareBar share={option.share} maxShare={maxShare} isKey={option.is_correct} />
      </td>

      <td className="tabular px-3 py-2 text-right align-top text-ink-muted">
        {option.chosen}
      </td>

      <td className="px-3 py-2 text-right align-top">
        {option.mean_score_pct === null ? (
          <span
            className="text-ink-faint"
            title="Nobody chose this option, so there is no mean score. This is not a mean of zero."
          >
            —
          </span>
        ) : (
          <span
            className={cn(
              "tabular font-medium",
              beatsKey ? "text-violation" : option.is_correct ? "text-answered" : "text-ink",
            )}
          >
            {option.mean_score_pct.toFixed(1)}%
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * Share of those who answered, drawn by hand because the app carries no chart
 * library and this is one rectangle.
 *
 * Bars are scaled to the widest option rather than to 100%, so on a question
 * where the answers split four ways the differences are still visible. The
 * percentage is printed beside it, so the bar is a scanning aid and never the
 * only carrier of the value.
 */
function ShareBar({
  share,
  maxShare,
  isKey,
}: {
  share: number;
  maxShare: number;
  isKey: boolean;
}) {
  const width = maxShare > 0 ? Math.max(share / maxShare, 0) : 0;
  return (
    <div className="flex items-center gap-2">
      <svg
        width="72"
        height="8"
        viewBox="0 0 72 8"
        role="img"
        aria-label={`${formatPercent(share)} of those who answered`}
        className="shrink-0"
      >
        <rect x="0" y="1" width="72" height="6" rx="1" fill="var(--color-neutral-soft)" />
        <rect
          x="0"
          y="1"
          width={Math.max(width * 72, share > 0 ? 1.5 : 0)}
          height="6"
          rx="1"
          fill={isKey ? "var(--color-answered)" : "var(--color-border-strong)"}
        />
      </svg>
      <span className="tabular text-2xs text-ink-muted">{formatPercent(share)}</span>
    </div>
  );
}
